import * as React from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import { Delta } from '../../../dashboard/components/Delta';

const calculatePercent = (part: number, total: number): number => {
    if (!total) {
        return 0;
    }
    return (part / total) * 100;
};

type Props = {
    totalSuccesses: number;
    totalFailures: number;
    totalSuccessesPrevious: number;
    totalFailuresPrevious: number;
};

export function StatsKpi(props: Props) {
    const {
        totalSuccesses,
        totalFailures,
        totalSuccessesPrevious,
        totalFailuresPrevious,
    } = props;

    const totalCount = totalSuccesses + totalFailures;
    const totalCountPrev = totalSuccessesPrevious + totalFailuresPrevious;
    const successPercent = calculatePercent(totalSuccesses, totalCount);
    const successPercentPrev = calculatePercent(totalSuccessesPrevious, totalCountPrev);
    const failPercent = calculatePercent(totalFailures, totalCount);
    const failPercentPrev = calculatePercent(totalFailuresPrevious, totalCountPrev);
    const successColor = successPercent >= successPercentPrev ? 'success.600' : 'danger.600';
    const failColor = failPercent <= failPercentPrev ? 'success.600' : 'danger.600';

    return (
        <Sheet
            variant="outlined"
            sx={{
                p: 2,
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                justifyContent: 'space-between',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SmartToyRoundedIcon fontSize="small" />
                <Typography level="title-md">Статистика обменов · Обмен через ботов</Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography level="body-sm">Всего обменов</Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            columnGap: 1,
                            rowGap: 0.25,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Tooltip
                            title="Всего обменов за выбранный (прошлый) периоды"
                            disablePortal={false}
                        >
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    columnGap: 1,
                                }}
                            >
                                <Typography
                                    level="title-lg"
                                    sx={{
                                        color:
                                            totalCount >= totalCountPrev
                                                ? 'success.600'
                                                : 'danger.600',
                                    }}
                                >
                                    +{totalCount.toString()}
                                </Typography>
                                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                                    (+{totalCountPrev})
                                </Typography>
                            </Box>
                        </Tooltip>
                        <Tooltip
                            title="Прирост относительно прошлого периода"
                            disablePortal={false}
                        >
                            <Box sx={{ ml: 0.5 }}>
                                <Delta
                                    current={totalCount}
                                    previous={totalCountPrev}
                                    hideZeroPercent
                                />
                            </Box>
                        </Tooltip>
                    </Box>
                </Box>

                <Box>
                    <Typography level="body-sm">Успешные</Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            columnGap: 1,
                            rowGap: 0.25,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Tooltip
                            title="Успешные обмены за выбранный (прошлый) периоды"
                            disablePortal={false}
                        >
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    columnGap: 1,
                                }}
                            >
                                <Typography
                                    level="title-lg"
                                    sx={{
                                        color:
                                            totalSuccesses >= totalSuccessesPrevious
                                                ? 'success.600'
                                                : 'danger.600',
                                    }}
                                >
                                    +{totalSuccesses}
                                </Typography>
                                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                                    (+{totalSuccessesPrevious})
                                </Typography>
                            </Box>
                        </Tooltip>
                        <Tooltip
                            title="Прирост относительно прошлого периода"
                            disablePortal={false}
                        >
                            <Box sx={{ ml: 0.5 }}>
                                <Delta
                                    current={totalSuccesses}
                                    previous={totalSuccessesPrevious}
                                    hideZeroPercent
                                />
                            </Box>
                        </Tooltip>
                    </Box>
                </Box>

                <Box>
                    <Typography level="body-sm">Неуспешные</Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            columnGap: 1,
                            rowGap: 0.25,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Tooltip
                            title="Неуспешные обмены за выбранный (прошлый) периоды"
                            disablePortal={false}
                        >
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    columnGap: 1,
                                }}
                            >
                                <Typography
                                    level="title-lg"
                                    sx={{
                                        color:
                                            totalFailures <= totalFailuresPrevious
                                                ? 'success.600'
                                                : 'danger.600',
                                    }}
                                >
                                    +{totalFailures}
                                </Typography>
                                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                                    (+{totalFailuresPrevious})
                                </Typography>
                            </Box>
                        </Tooltip>
                        <Tooltip
                            title="Прирост относительно прошлого периода"
                            disablePortal={false}
                        >
                            <Box sx={{ ml: 0.5 }}>
                                <Delta
                                    current={totalFailures}
                                    previous={totalFailuresPrevious}
                                    invertColor
                                    hideZeroPercent
                                />
                            </Box>
                        </Tooltip>
                    </Box>
                </Box>

                <Box sx={{ flexBasis: '100%', width: '100%' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                            alignItems: 'flex-start',
                        }}
                    >
                        <Box>
                            <Typography level="body-sm">Процент успешных</Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    columnGap: 1,
                                    rowGap: 0.25,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Tooltip
                                    title="Процент успешных обменов за выбранный (прошлый) периоды"
                                    disablePortal={false}
                                >
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            columnGap: 1,
                                        }}
                                    >
                                        <TrendingUpRoundedIcon
                                            fontSize="small"
                                            sx={{ color: successColor }}
                                        />
                                        <Typography
                                            level="title-lg"
                                            sx={{ color: successColor }}
                                        >
                                            {successPercent.toFixed(2)}%
                                        </Typography>
                                        <Typography
                                            level="body-sm"
                                            sx={{ color: 'neutral.500' }}
                                        >
                                            ({successPercentPrev.toFixed(2)}%)
                                        </Typography>
                                    </Box>
                                </Tooltip>
                                <Tooltip
                                    title="Прирост относительно прошлого периода"
                                    disablePortal={false}
                                >
                                    <Box sx={{ ml: 0.5 }}>
                                        <Delta
                                            current={successPercent}
                                            previous={successPercentPrev}
                                        />
                                    </Box>
                                </Tooltip>
                            </Box>
                        </Box>

                        <Box>
                            <Typography level="body-sm">Процент неуспешных</Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    columnGap: 1,
                                    rowGap: 0.25,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Tooltip
                                    title="Процент неуспешных обменов за выбранный (прошлый) периоды"
                                    disablePortal={false}
                                >
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            columnGap: 1,
                                        }}
                                    >
                                        <TrendingUpRoundedIcon
                                            fontSize="small"
                                            sx={{ color: failColor }}
                                        />
                                        <Typography
                                            level="title-lg"
                                            sx={{ color: failColor }}
                                        >
                                            {failPercent.toFixed(2)}%
                                        </Typography>
                                        <Typography
                                            level="body-sm"
                                            sx={{ color: 'neutral.500' }}
                                        >
                                            ({failPercentPrev.toFixed(2)}%)
                                        </Typography>
                                    </Box>
                                </Tooltip>
                                <Tooltip title="Изменение относительно прошлого периода (меньше — лучше)" disablePortal={false}>
                                    <Box sx={{ ml: 0.5 }}>
                                        <Delta current={failPercent}
                                               previous={failPercentPrev}
                                               invertColor
                                               hideZeroPercent />
                                    </Box>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Sheet>
    );
}
